// ----------------------------------------------------------------------
// <copyright file="Izenda.ConnectionDetailStore.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { isEmpty, isNil, get, getOr } from 'lodash/fp';

import { action, computed } from 'mobx';
import dataFetcher from 'fetchers/Izenda.ConnectionStringDataFetcher';
import uiState from 'logic/Izenda.UiState.js';
import constants from 'logic/data/Izenda.ConnectionConstants';
import localizer from 'share/localization/Izenda.Localizer';
import ConnectionStore from './Izenda.ConnectionStore';
import ConnectionUtils from 'modules/Settings/DataSetup/Connection/Izenda.ConnectionUtils';

export default class ConnectionDetailStore {

  masterStore: ConnectionStore = null;

  /**
   *  It encapsulates the connection detail app logic (side-effect likes API invoke
   *  and UI logic)
   * @param {ConnectionStore} masterStore
   */
  constructor(masterStore) {
    this.masterStore = masterStore;
  }

  /**
   * Current connection detail model from master store
   * @returns {ConnectionDetail}
   */
  @computed get item() {
    return this.masterStore.item;
  }

  /**
   * List of available DB types
   * @returns {Object[]}
   */
  @computed get dbTypeList() {
    return this.masterStore.dbTypeList;
  }

  /**
   * Tenant Id
   * @returns {string}
   */
  @computed get tenantId() {
    return uiState.getTenantId();
  }

  /**
   * link between store and uiState\
   */
  @action init() {
    uiState.setCheckDirtyFunction(() => this.item.needConfirmWhenLeaving);
  };

  /**
   * unlink between store and uiState\
   */
  @action destroy() {
    this.masterStore.deleteTempExcelFiles();
    uiState.resetCheckDirtyFunction();
  }

  /**
   * Load connection data by its id from API
   * @return {Promise<boolean>} A promise of loading connection data
   */
  @action
  load(id) {
    const currentId = id || this.item.id;
    if (isEmpty(currentId)) {
      // If it is new (its id is emtpy), just read its default detail.
      return Promise.resolve(true);
    }

    // Only load connection if its id existed.
    return dataFetcher
      .getConnectionString(currentId)
      .then(get('connection'))
      .then(action(detail => {
        const connectionDetail = this.item;
        connectionDetail.setConnectionStringStatus();
        connectionDetail.readDetail(detail);
        return detail;
      }))
      .then(detail => {
        this.connectToDataSource(detail);
        return null;
      });
  }

  @action reloadDetectChanges() {
    const connectionDetail = this.item;
    if (isEmpty(connectionDetail.id)) {
      return;
    }

    const updateChanges = detail => {
      connectionDetail.setChange(detail.physicalChange);
      uiState.increaseDetectChangeCount();
      return detail;
    };

    // Only load connection if its id existed.
    return dataFetcher
      .getConnectionString(connectionDetail.id)
      .then(get('connection'))
      .then(updateChanges);
  };

  /**
   * Connect to data source by input connection string in order to getting
   * its database schema including its physical shema changes against previous
   * saved schema.
   * @param  {ConnectionDetail} detail
   * @param  {bool} force  Force re-load data source regardless to its existing one.
   */
  @action connectToDataSource(detail, force) {
    const isEmptyDataSource = isEmpty(detail.dbSource);
    const needReloadDetectChange = detail.needReloadDetectChange;

    // Retrieve data source from API as current data source is empty or force to do so
    let retrieveDataSource;
    if (!isEmptyDataSource && !force) {
      // Don't need to call api when we already have some dbSource and force is turned off
      retrieveDataSource = Promise.resolve({
        dBSource: detail.dbSource
      });
    } else {
      // need to call api
      retrieveDataSource = !needReloadDetectChange
          ? dataFetcher.getRemoteSchema(detail.connectionString, detail.serverTypeId)
          : dataFetcher.reloadRemoteSchema(detail);
    }

    return retrieveDataSource.then(action(result => {
      if (result.success === false) {
        this.updateErrorMessage(result);
      } else {
        this.item.readDbSource(result.dBSource);
        if ((!isEmptyDataSource || force) && needReloadDetectChange) {
          // reload change to detail & notify changes to contextmenu
          this.reloadDetectChanges();
        }
      }
      return result;
    }));
  }

  /**
   * Load preset for connection detail connection string builder.
   * @param {string} serverTypeId
   * @returns {Promise}
   */
  loadConnectionStringPreset(serverTypeId) {
    return dataFetcher
      .getConnectionStringPreset(serverTypeId);
  }

  /**
   * Load data to refill current connection with data from connection string.
   * @param {ConnectionDetail} connectionDetail
   * retunr {Promise}
   */
  loadConnectionStringData(connectionDetail) {
    if (!connectionDetail) {
      return Promise.reject();
    }
    if (isEmpty(connectionDetail.connectionString)) {
      return Promise.resolve({
        success: true,
        data: null
      })
    }
    return dataFetcher
      .getConnectionStringData({
        connectionId: connectionDetail.id,
        serverTypeId: connectionDetail.serverTypeId,
        serverTypeName: connectionDetail.serverTypeName,
        connectionString: connectionDetail.connectionString
      });
  }

  /**
   * Get URL for uploading data files.
   */
  getUploadUrl() {
    return dataFetcher.UPLOAD_URL;
  }

  /**
   * Populate database name by current connection string when user doesn't
   * enter its name.
   * @return {Promise} promise param - pass through current REST result
   */
  @action populateDatabaseName() {
    const connDetail = this.item;
    const isRest = ConnectionUtils.isDbType(connDetail.serverTypeId, 'REST', this.masterStore);
    return !isRest && isEmpty(connDetail.name)
      ? getDbNameApi(connDetail)
        .then(
          result => {
            if (result.success && !isEmpty(result.data)) {
              connDetail.setName(result.data);
            }
            return result;
          })
      : Promise.resolve({ success: true });
  }

  /**
   * Do full connection steps
   * @returns {Promise} promise with REST result argument {success:..., messages:...}
   */
  @action performConnection(connectionString) {
    return new Promise(async resolve => {
      try {
        this.item.setConnectionString(connectionString);

        // "connection/DatabaseName" API call
        const populateResult = await this.populateDatabaseName();
        if (!populateResult.success) {
          resolve(populateResult);
          return;
        }

        // "connection/verify" API call
        const resultValidateConnection = await this.validateConnection();
        if (!resultValidateConnection.success) {
          resolve(resultValidateConnection);
          return;
        }

        // Check if connection string is duplicated
        if (this.item.isCurrentConnectionStringSameAsSavedConnectionString) {
          resolve({
            success: false,
            messages: [{
              key: 'connectionString',
              messages: [localizer.getText('CONNECTION_IS_DUPLICATED')]
            }]
          });
          return;
        }

        // "connection/validateSchema" API call (promise returns bool)
        const resultValidateSchema = await this.validateSchema();

        // Connect to data source (promise returns bool)
        const result = await this.connectToDataSource(this.item, true);
        resolve(result);

      } catch (e) {
        // Handle internal server error
        if (e && !isNil(e.status) && e.status !== 200) {
          resolve({
            success: false,
            messages: [{
              key: '',
              messages: [getOr(e.statusText, 'responseJSON.message', e)]
            }]
          });
          return;
        }

        const errorMessages = getOr(null, 'messages', e);
        if (errorMessages && errorMessages.length) {
          resolve({
            success: false,
            messages: errorMessages
          });
        } else {
          throw new Error('Unknown error: ', e);
        }
      }
    });
  }

  /**
   * Validate current connection string
   * @return {Promise}
   */
  validateConnection() {
    const connectionDetail = this.item;
    if (isEmpty(connectionDetail.connectionString)) {
      return Promise.resolve({
        success: false,
        messages: [{
          key: 'connectionString',
          messages: [localizer.getText('CONNECTION_REQUIRE_CONNECTION_STRING')]
        }]
      });
    }
    // Run test connection api
    return testConnectionApi(connectionDetail).then(result => {
      connectionDetail.readTestResult(result);
      connectionDetail.setConnectionStringValid(result.success);
      return result;
    });
  }

  validateSchema() {
    const connectionDetail = this.item;
    if (connectionDetail.notNeedToValidateSchema) {
      return Promise.resolve(true);
    } else if (connectionDetail.isNew) {
      return Promise.resolve(false);
    }

    return validateSchemaApi(connectionDetail).then(success => {
      connectionDetail.readStatusValidateSchema(success);
      return success;
    });
  };

  /**
   * Save connection details
   * @returns {*}
   */
  @action('save') save() {
    const data = this.item.getData();
    data.tenantId = uiState.getTenantId();
    return dataFetcher
      .updateConnectionString(data)
      .then(action((result) => {
        if (result.success) {
          // Update connection string
          this.item.connectionString = result.connectionString;
          // Delete temp excel files after saved
          this.masterStore.deleteTempExcelFiles();
        }

        if (result.success && isEmpty(data.id)) {
          // Get server type short name from the list and assign back to
          // current connection detail in case of adding new connection.
          const serverType = this.dbTypeList.find(item => item.id === data.serverTypeId);
          this.item.serverTypeName = serverType.shortName;
          this.item.id = result.connectionId;
          this.masterStore.addConnectionToList(this.item);
          uiState.increaseDetectChangeVisibleConnection();
        } else {
          /**
           * If this connection has changes:
           *   - Reload datasourcetree
           *   - Reload change to detail
           *   - Notify changes to contextmenu
           */
          if (this.item.change !== constants.DETECTED_CHANGE_LEVEL.NONE) {
            this.connectToDataSource(this.item, true);
          }
        }

        if (result.success) {
          // in case of add new connection string
          // then re-load to get connection string encryption
          this.load();
          this.masterStore.loadList(uiState.tenantId);
          this.item.resetDirty();
          uiState.increaseDetectChangeCount();
          this.item.resetAvailableDataSourceChange();
          this.item.setAlertMessage({
            type: 'success',
            message: localizer.getText('COMMON_MESSAGES_DETAILS_WERE_SAVED')
          });
        } else {
          this.updateErrorMessage(result);
        }

        return result;
      }));
  }

  /**
   * update error message
   * @param  {Object} result
   */
  updateErrorMessage(result) {
    const messageError = !isEmpty(result.messages) && result.messages[0].key === ''
      ? result.messages[0].messages[0]
      : null;
    this.item.setAlertMessage({ type: 'error', message: messageError });
  }
}

/**
 * Call testConnectionString API
 * @param  {ConnectionDetail} connectionDetail
 * @return {Promise} A promise of API invoke
 */
function testConnectionApi(connectionDetail) {
  const connectionStringRequest = {
    ServerTypeId: connectionDetail.serverTypeId,
    ConnectionString: connectionDetail.connectionString,
    ConnectionId: connectionDetail.id
  };
  return dataFetcher.testConnectionString(connectionStringRequest);
}

/**
 * Call validateSchemaApi API
 * @param  {ConnectionDetail} connectionDetail
 * @return {Promise} A promise of API invoke
 */
function validateSchemaApi(connectionDetail) {
  const connectionStringRequest = connectionDetail.getData(true);
  return dataFetcher.validateSchema(connectionStringRequest);
}

/**
 * Call getDatabaseName API
 * @param  {ConnectionDetail} connectionDetail
 * @return {Promise} A promise of API call
 */
function getDbNameApi(connectionDetail) {
  const getDatabaseNameRequest = {
    ServerTypeId: connectionDetail.serverTypeId,
    ConnectionString: connectionDetail.connectionString
  };
  return dataFetcher.getDatabaseName(getDatabaseNameRequest);
}



// WEBPACK FOOTER //
// ./ui/logic/Izenda.ConnectionDetailStore.js